"use client";

import {
  AD_DETAILS_2_COLUMN_MAX_WIDTH,
  AD_DETAILS_ASIDE_WIDTH,
  AD_DETAILS_BODY_WIDTH,
  AD_DETAILS_BORDER_RADIUS,
} from "@/app/layout/LayoutConstants";
import { Box, styled } from "@mui/material";
import { ReactNode } from "react";
import { DETAILS_ADS } from "../ads";
import AdModule from "@/components/shared/AdModule";
import { useTopExited } from "@/app/layout/useTopExited";

export const AD_DETAILS_DESKTOP_SPACING = 4;
export const AD_DETAILS_MOBILE_SPACING = 1;

const HiddenAd = styled(Box)(({ theme }) =>
  theme.unstable_sx({
    height: theme.spacing(4),
    backgroundColor: "grey.100",
    [theme.breakpoints.down(AD_DETAILS_2_COLUMN_MAX_WIDTH)]: {
      display: "none",
    },
  }),
);

const AdDetailsGridContainer = styled(Box)(({ theme }) =>
  theme.unstable_sx({
    display: "grid",
    gridTemplateColumns: `minmax(${theme.spacing(2)}, 1fr) minmax(400px, ${AD_DETAILS_BODY_WIDTH}) ${AD_DETAILS_ASIDE_WIDTH} minmax(${theme.spacing(2)}, 1fr)`,
    width: "100%",
    backgroundColor: "grey.100",
    [theme.breakpoints.down(AD_DETAILS_2_COLUMN_MAX_WIDTH)]: {
      gridTemplateColumns: "100%",
    },
  }),
);

export default function AdDetailsPageLayout({
  actions,
  gallery,
  prebody,
  body,
  footer,
  aside,
  hideAds,
}: {
  actions: ReactNode;
  gallery: ReactNode;
  prebody: ReactNode;
  body: ReactNode;
  footer: ReactNode;
  aside: ReactNode;
  hideAds: boolean;
}) {
  const [targetRef, hasExitedTop] = useTopExited();
  return (
    <Box>
      {hideAds ? (
        <HiddenAd />
      ) : (
        <AdModule config={DETAILS_ADS.Horsezone_Details_Top} />
      )}
      <AdDetailsGridContainer>
        {gallery}
        <Box
          sx={(theme) => ({
            gridColumn: "2",
            backgroundColor: "white",
            maxWidth: "100vw",
            borderBottomLeftRadius: theme.spacing(AD_DETAILS_BORDER_RADIUS),
            borderBottomRightRadius: theme.spacing(AD_DETAILS_BORDER_RADIUS),
            paddingTop: AD_DETAILS_DESKTOP_SPACING,
            [theme.breakpoints.down(AD_DETAILS_2_COLUMN_MAX_WIDTH)]: {
              gridColumn: "1",
              borderRadius: 0,
            },
          })}
        >
          {prebody}
          <Box
            ref={targetRef}
            sx={{
              display: "flex",
              flexDirection: "column",
              height: "100%",
              gap: 4,
              justifyContent: "space-between",
            }}
          >
            <Box
              sx={(theme) => ({
                paddingLeft: AD_DETAILS_DESKTOP_SPACING,
                paddingRight: AD_DETAILS_DESKTOP_SPACING,
                [theme.breakpoints.down(AD_DETAILS_2_COLUMN_MAX_WIDTH)]: {
                  paddingLeft: AD_DETAILS_MOBILE_SPACING,
                  paddingRight: AD_DETAILS_MOBILE_SPACING,
                },
              })}
            >
              {body}
            </Box>
            <Box
              sx={(theme) => ({
                position: "sticky",
                bottom: 0,
                backgroundColor: "white",
                boxShadow: "0 -8px 6px -6px rgba(0, 0, 0, 0.3)",
                zIndex: 10,
                transition: theme.transitions.create("bottom", {
                  duration: theme.transitions.duration.standard,
                  easing: theme.transitions.easing.easeInOut,
                }),
                [theme.breakpoints.up(AD_DETAILS_2_COLUMN_MAX_WIDTH)]: {
                  bottom: hasExitedTop ? 0 : "-100%",
                  borderBottomRightRadius: theme.spacing(
                    AD_DETAILS_BORDER_RADIUS,
                  ),
                  borderBottomLeftRadius: theme.spacing(
                    AD_DETAILS_BORDER_RADIUS,
                  ),
                },
              })}
            >
              {actions}
            </Box>
          </Box>
        </Box>
        <Box
          sx={(theme) => ({
            gridColumn: 2,
            display: "flex",
            justifyContent: "flex-end",
            marginTop: 4,
            [theme.breakpoints.down(AD_DETAILS_2_COLUMN_MAX_WIDTH)]: {
              marginTop: 0,
              gridColumn: 1,
              padding: 1,
            },
          })}
        >
          {footer}
        </Box>
        <Box
          sx={(theme) => ({
            gridColumn: 3,
            gridRow: 2,
            display: "flex",
            flexDirection: "column",
            gap: 6,
            [theme.breakpoints.down(AD_DETAILS_2_COLUMN_MAX_WIDTH)]: {
              gridColumn: 1,
              gridRow: 5,
              gap: AD_DETAILS_MOBILE_SPACING,
            },
          })}
        >
          {aside}
        </Box>
      </AdDetailsGridContainer>
      {hideAds ? (
        <HiddenAd />
      ) : (
        <AdModule config={DETAILS_ADS.Horsezone_Details_Bottom} />
      )}
    </Box>
  );
}
